import { useCallback } from 'react';

import { InlineFileCard } from '@/lib/base/file-upload';
import { getApiBaseUrl } from '@/lib/config';
import type { EmailAttachmentMeta } from '@/lib/crm/record/timeline/types';
import { cx } from '@/utils/cx';

export interface EmailAttachmentListProps {
  /** Attachment entries from the email event payload (already name-filtered). */
  attachments: EmailAttachmentMeta[];
  /** Org scope for the download URL. */
  orgId: number;
  /**
   * Chip density: `sm` (default) for message bodies (thread view, preview
   * modal); `xs` for dense surfaces like timeline event cards.
   */
  variant?: 'sm' | 'xs';
  /** Additional class name for the wrapping row. */
  className?: string;
}

interface EmailAttachmentChipProps {
  attachment: EmailAttachmentMeta;
  orgId: number;
  variant: 'sm' | 'xs';
}

/**
 * Trigger a browser download of a stored email attachment via an anchor click.
 *
 * Mirrors `useRecordAttachmentDownloads.downloadFile`: a same-gesture anchor
 * navigation to the org-scoped download endpoint, which carries the session
 * cookie and enforces per-user readability of the file's `ahoy_file` entity.
 */
function downloadAttachment(orgId: number, attachment: EmailAttachmentMeta) {
  if (!attachment.uploaded_file_id) {
    return;
  }
  const url = `${getApiBaseUrl()}/api/orgs/${orgId}/files/${attachment.uploaded_file_id}/download/`;
  const a = document.createElement('a');
  a.href = url;
  a.download = attachment.name ?? '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function EmailAttachmentChip({
  attachment,
  orgId,
  variant,
}: EmailAttachmentChipProps) {
  const handleDownload = useCallback(() => {
    downloadAttachment(orgId, attachment);
  }, [orgId, attachment]);

  return (
    <InlineFileCard
      filename={attachment.name ?? ''}
      size={attachment.size ?? 0}
      mimeType={attachment.content_type ?? 'application/octet-stream'}
      onDownload={attachment.uploaded_file_id ? handleDownload : undefined}
      variant={variant}
    />
  );
}

/**
 * A wrapping row of attachment chips for an email surface (thread message card,
 * timeline email event, preview modal).
 *
 * Renders purely from payload metadata — chips never resolve `ahoy_file`
 * entities (a per-event fan-out the timeline must not pay). Entries without an
 * `uploaded_file_id` (bytes not persisted) render as inert metadata chips with
 * no download affordance. Renders nothing for an empty list.
 */
export function EmailAttachmentList({
  attachments,
  orgId,
  variant = 'sm',
  className,
}: EmailAttachmentListProps) {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <div
      className={cx(
        'flex flex-wrap',
        variant === 'xs' ? 'gap-1' : 'gap-1.5',
        className
      )}
    >
      {attachments.map((attachment, index) => (
        <EmailAttachmentChip
          // eslint-disable-next-line react/no-array-index-key -- entries come from an immutable event payload; identical attachments (same name/size/file id) are possible, so no field combination is unique without the index
          key={`${attachment.uploaded_file_id ?? attachment.id ?? attachment.name ?? ''}:${index}`}
          attachment={attachment}
          orgId={orgId}
          variant={variant}
        />
      ))}
    </div>
  );
}
